import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { Building2, MapPin, Calendar, Upload, FileText, Loader, Folder, AlertCircle, Wrench, User, Phone, Mail, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { InfoButton } from '../../components/InfoModal';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import CsApplyPreview from '../../components/CsApplyPreview';
import BuildingSsSection from '../../components/BuildingSsSection';
import FloorStructureSetup from '../../components/FloorStructureSetup';
import { formatDateForInput, normalizeBuildingDatesForSave, computeNextBwofDueFromAnniversary } from '../../utils/formatDateForInput';
import { sortBySsCode } from '../../utils/ssCodeSort';

/** Stable empty list so a disabled query does not change effect deps every render. */
const EMPTY_LIST = [];

function sameDeviceChecks(prev, next) {
  const prevKeys = Object.keys(prev || {});
  const nextKeys = Object.keys(next || {});
  if (prevKeys.length !== nextKeys.length) return false;
  return nextKeys.every((key) => !!prev[key] === !!next[key]);
}

/** Timed ISO from MySQL is a local calendar day, not the UTC date prefix. */
function contractStartForInput(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return formatDateForInput(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildingToFormData(building) {
  if (!building) return null;
  const scope = String(building.agency_scope || '').toUpperCase();
  const pa = building.partly_agent || null;
  return {
    name: building.name || '',
    address: building.address || '',
    due_date: formatDateForInput(building.due_date),
    compliance_schedule_anniversary: formatDateForInput(building.compliance_schedule_anniversary),
    initial_inspection_status: building.initial_inspection_status || '',
    compliance_schedule_number: building.compliance_schedule_number || '',
    owner_name: building.owner_name || '',
    owner_mailing_address: building.owner_mailing_address || '',
    owner_email: building.owner_email || '',
    owner_telephone_work: building.owner_telephone_work || '',
    latitude: building.latitude || '',
    longitude: building.longitude || '',
    floor_plan_url: building.floor_plan_url || '',
    worker_can_move_unit_markers: !!building.worker_can_move_unit_markers,
    is_default_for_workers: !!building.is_default_for_workers,
    has_chiller: !!building.has_chiller,
    is_bwof_agent: !!building.is_bwof_agent || scope === 'FULL_AGENT',
    agency_scope: scope === 'FULL_AGENT' || scope === 'SS_ONLY' ? scope : '',
    operation_start_date: contractStartForInput(building.operation_start_date),
    contract_end_date: formatDateForInput(building.contract_end_date),
    partly_agent_id: building.partly_agent_id || '',
    partly_agent_name: pa?.name || '',
    partly_agent_phone: pa?.phone || '',
    partly_agent_email: pa?.email || '',
    partly_agent_address: pa?.address || '',
  };
}

function BuildingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = id != null && id !== 'new';

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    due_date: '',
    compliance_schedule_anniversary: '',
    initial_inspection_status: '',
    compliance_schedule_number: '',
    owner_name: '',
    owner_mailing_address: '',
    owner_email: '',
    owner_telephone_work: '',
    latitude: '',
    longitude: '',
    floor_plan_url: '',
    worker_can_move_unit_markers: false,
    is_default_for_workers: false,
    has_chiller: false,
    is_bwof_agent: false,
    agency_scope: '',
    operation_start_date: '',
    contract_end_date: '',
    partly_agent_id: '',
    partly_agent_name: '',
    partly_agent_phone: '',
    partly_agent_email: '',
    partly_agent_address: '',
  });
  const [agencyScopeAutoPartly, setAgencyScopeAutoPartly] = useState(false);
  const [coverageRev, setCoverageRev] = useState(0);
  const ssSectionRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [analyzingPdf, setAnalyzingPdf] = useState(false);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState('');
  const [pendingCsExtract, setPendingCsExtract] = useState(null);
  const [pendingCsPdf, setPendingCsPdf] = useState(null);
  const [csDbResult, setCsDbResult] = useState(null); // last successful DB apply summary
  const [seedCsChecklists, setSeedCsChecklists] = useState(true);
  const [confirmingCs, setConfirmingCs] = useState(false);
  const [showSavedPdfs, setShowSavedPdfs] = useState(false);
  const [showFolders, setShowFolders] = useState(false);
  const [deviceChecks, setDeviceChecks] = useState({});
  const [deviceChecksReady, setDeviceChecksReady] = useState(false);
  const deviceChecksTouched = useRef(false);
  const skipNextBuildingHydrate = useRef(false);

  const { data: building, isLoading } = useQuery({
    queryKey: ['building', id],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}`);
      return response.data;
    },
    enabled: isEdit && !!id,
    retry: false,
  });

  const { data: partlyAgents = [] } = useQuery({
    queryKey: ['partly-agents', 'picker'],
    queryFn: async () => (await api.get('/partly-agents')).data?.data || [],
  });

  // Fetch saved compliance schedule PDFs for this building
  const { data: savedPdfs } = useQuery({
    queryKey: ['building', id, 'compliance-schedules'],
    queryFn: async () => {
      try {
        const response = await api.get(`/buildings/${id}/compliance-schedules`);
        return response.data.data || [];
      } catch (error) {
        console.error('Failed to fetch saved PDFs:', error);
        return [];
      }
    },
    enabled: isEdit && !!id && id !== 'new',
    retry: false,
  });

  const { data: inspectionDevices = EMPTY_LIST } = useQuery({
    queryKey: ['inspection-modules', 'device-types'],
    queryFn: async () => (await api.get('/inspection-modules/device-types')).data?.data || [],
    staleTime: 30_000,
  });

  const { data: inspectionMenu = EMPTY_LIST } = useQuery({
    queryKey: ['building', id, 'inspection-menu'],
    queryFn: async () =>
      (await api.get(`/inspection-modules/buildings/${id}/menu`)).data?.data || [],
    enabled: isEdit && !!id,
  });

  useEffect(() => {
    if (deviceChecksTouched.current || !inspectionDevices.length) return;
    const on = new Set((inspectionMenu || []).map((row) => String(row.module_code || '').toUpperCase()));
    if (formData.has_chiller) on.add('CHILLER');
    const next = {};
    for (const device of inspectionDevices) {
      const code = String(device.module_code || '').toUpperCase();
      next[code] = on.has(code);
    }
    setDeviceChecks((prev) => (sameDeviceChecks(prev, next) ? prev : next));
    setDeviceChecksReady(true);
  }, [inspectionDevices, inspectionMenu, formData.has_chiller]);

  const { data: ssCodesData } = useQuery({
    queryKey: ['ss-codes'],
    queryFn: async ({ signal }) => {
      const response = await api.get('/workers/ss-codes', { signal });
      return response.data.data || [];
    },
    enabled: isEdit && !!id,
  });
  const ssCodes = useMemo(
    () => sortBySsCode(Array.isArray(ssCodesData) ? ssCodesData : [], 'code'),
    [ssCodesData]
  );

  // Building document settings (Edit Building → Documents)
  const {
    data: folderData,
    isError: folderError,
    error: folderErrObj,
    isFetching: folderFetching,
  } = useQuery({
    queryKey: ['building', id, 'folders'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}/folders`);
      return response.data;
    },
    enabled: isEdit && showFolders && !!id,
    retry: false,
  });

  const [docStorageRoot, setDocStorageRoot] = useState('');
  const [docEnabled, setDocEnabled] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    if (!folderData?.settings) return;
    setDocStorageRoot(folderData.settings.storage_root || folderData.default_storage_root || '');
    setDocEnabled(!!folderData.settings.enabled);
  }, [folderData]);

  const saveDocSettingsMutation = useMutation({
    mutationFn: async (payload) =>
      (await api.put(`/buildings/${id}/documents/settings`, payload)).data,
    onSuccess: () => {
      toast.success('Document settings saved');
      queryClient.invalidateQueries(['building', id, 'folders']);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save document settings'),
  });

  const addFolderMutation = useMutation({
    mutationFn: async (name) =>
      (await api.post(`/buildings/${id}/documents/folders`, { name })).data,
    onSuccess: () => {
      toast.success('Folder added');
      setNewFolderName('');
      queryClient.invalidateQueries(['building', id, 'folders']);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to add folder'),
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (folderPath) =>
      api.delete(`/buildings/${id}/documents/folders`, { params: { path: folderPath } }),
    onSuccess: () => {
      toast.success('Folder removed');
      queryClient.invalidateQueries(['building', id, 'folders']);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete folder'),
  });

  useEffect(() => {
    if (!isEdit || isLoading) return;
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
    return () => clearTimeout(t);
  }, [isEdit, isLoading, id]);

  useEffect(() => {
    if (skipNextBuildingHydrate.current) {
      skipNextBuildingHydrate.current = false;
      return;
    }
    // Don't wipe PDF-filled inputs while extract is pending review / apply
    if (pendingCsExtract) return;
    if (isEdit && building && id !== 'new') {
      const next = buildingToFormData(building);
      if (next) {
        setFormData(next);
        if (next.agency_scope === 'FULL_AGENT' || next.agency_scope === 'SS_ONLY') {
          setAgencyScopeAutoPartly(false);
        }
      }
    }
  }, [building, isEdit, id, pendingCsExtract]);

  /**
   * Full = we cover the whole building. Partial = selected SS only. External Agent details are optional.
   * driveSs pushes that choice onto the SS list (and a pending CS preview). Checkbox auto-switch
   * calls this with driveSs false so it does not wipe the ticks the user just set.
   */
  const applyCoverage = (mode, { driveSs = false } = {}) => {
    const partial = mode === 'PARTIAL';
    setAgencyScopeAutoPartly(partial);
    setFormData((prev) => ({
      ...prev,
      agency_scope: partial ? 'SS_ONLY' : 'FULL_AGENT',
      is_bwof_agent: !partial,
      ...(partial
        ? {}
        : {
            partly_agent_id: '',
            partly_agent_name: '',
            partly_agent_phone: '',
            partly_agent_email: '',
            partly_agent_address: '',
          }),
    }));
    setPendingCsExtract((prev) => {
      if (!prev) return prev;
      const rows =
        driveSs && Array.isArray(prev.ss_instances)
          ? prev.ss_instances.map((row) => ({
              ...row,
              include_in_inspection: partial ? 0 : 1,
            }))
          : prev.ss_instances;
      return {
        ...prev,
        inspection_scope: partial ? 'PARTIAL' : 'FULL',
        ss_instances: rows,
      };
    });
    if (driveSs) setCoverageRev((n) => n + 1);
  };

  const mergeExtractedIntoForm = (extractedData) => {
    skipNextBuildingHydrate.current = true;
    const csDate = extractedData.compliance_schedule_anniversary || extractedData.cs_issue_date || '';
    const dates = csDate
      ? normalizeBuildingDatesForSave(csDate, '', { autoComputeDue: true })
      : normalizeBuildingDatesForSave('', extractedData.due_date || '', { autoComputeDue: true });

    setFormData((prev) => {
      const updated = { ...prev };
      // CS upload: always prefer extracted values when present (related screens need PDF truth)
      const fill = (formKey, value) => {
        if (value === undefined || value === null || value === '') return;
        updated[formKey] = value;
      };
      fill('compliance_schedule_number', extractedData.compliance_schedule_number);
      fill('name', extractedData.building_name);
      fill('address', extractedData.address);
      fill('owner_name', extractedData.owner_name);
      fill('owner_mailing_address', extractedData.owner_mailing_address);
      fill('owner_email', extractedData.owner_email);
      fill('owner_telephone_work', extractedData.owner_telephone_work);

      if (dates.compliance_schedule_anniversary) {
        updated.compliance_schedule_anniversary = dates.compliance_schedule_anniversary;
      }
      if (dates.due_date) {
        updated.due_date = dates.due_date;
      }

      return updated;
    });
  };

  /** Drop debug/preview fields so Save/apply payloads stay small and stable */
  const slimCsExtract = (extractedData) => {
    if (!extractedData || typeof extractedData !== 'object') return extractedData;
    const {
      rawTextPreview,
      _dates_debug,
      _meta,
      ...rest
    } = extractedData;
    return rest;
  };

  const applyCsExtractToDb = async (extract, pdfMeta, buildingIdForApply) => {
    const bid = buildingIdForApply || id;
    if (!bid || bid === 'new' || !extract) return null;
    const applyRes = await api.post(`/buildings/${bid}/apply-compliance-schedule`, {
      extracted: extract,
      pdfPath: pdfMeta?.path || extract.uploaded_pdf_path || null,
      pdfFilename: pdfMeta?.filename || extract.uploaded_pdf_filename || null,
      extractMethod: extract.extract_method || null,
      seedChecklists: seedCsChecklists,
    });
    const {
      ssCreated,
      ssUpdated,
      unitsCreated,
      checklistsSeeded,
      unitsTotal,
      ssTotal,
      skippedSs,
      building: appliedBuilding,
      applied_fields: appliedFields,
    } = applyRes.data || {};
    skipNextBuildingHydrate.current = true;
    if (appliedBuilding) {
      const next = buildingToFormData(appliedBuilding);
      if (next) {
        setFormData((prev) => {
          const merged = { ...prev };
          for (const [key, val] of Object.entries(next)) {
            if (typeof val === 'boolean') {
              merged[key] = val;
            } else if (val !== undefined && val !== null && val !== '') {
              merged[key] = val;
            }
          }
          // Prefer extract for fields apply may have skipped (empty PDF → empty DB)
          const fill = (formKey, value) => {
            if (value === undefined || value === null || value === '') return;
            if (!merged[formKey]) merged[formKey] = value;
          };
          fill('compliance_schedule_number', extract.compliance_schedule_number);
          fill('name', extract.building_name);
          fill('address', extract.address);
          fill('owner_name', extract.owner_name);
          fill('owner_mailing_address', extract.owner_mailing_address);
          fill('owner_email', extract.owner_email);
          fill('owner_telephone_work', extract.owner_telephone_work);
          const csDate = extract.compliance_schedule_anniversary || extract.cs_issue_date || '';
          const dates = csDate
            ? normalizeBuildingDatesForSave(csDate, '', { autoComputeDue: true })
            : normalizeBuildingDatesForSave('', extract.due_date || '', { autoComputeDue: true });
          if (dates.compliance_schedule_anniversary && !merged.compliance_schedule_anniversary) {
            merged.compliance_schedule_anniversary = dates.compliance_schedule_anniversary;
          }
          if (dates.due_date && !merged.due_date) {
            merged.due_date = dates.due_date;
          }
          return merged;
        });
      }
    }
    await queryClient.invalidateQueries(['building', bid]);
    await queryClient.invalidateQueries(['building', bid, 'compliance-schedules']);
    await queryClient.invalidateQueries(['building', bid, 'ss-units']);
    await queryClient.invalidateQueries(['building', bid, 'ss-instances']);
    const summary = {
      ssCreated: ssCreated || 0,
      ssUpdated: ssUpdated || 0,
      unitsCreated: unitsCreated || 0,
      checklistsSeeded: checklistsSeeded || 0,
      unitsTotal: unitsTotal || 0,
      ssTotal: ssTotal || 0,
      skippedSs: skippedSs || [],
      appliedFields: appliedFields || [],
    };
    setCsDbResult(summary);
    return summary;
  };

  const processCsExtract = async (extractedData, buildingIdForApply, pdfMeta) => {
    const slim = slimCsExtract(extractedData);
    // Default Full: every extracted SS included until user picks Partial
    if (Array.isArray(slim.ss_instances) && slim.ss_instances.length) {
      slim.ss_instances = slim.ss_instances.map((r) => ({
        ...r,
        include_in_inspection:
          r.include_in_inspection === 0 ||
          r.include_in_inspection === false ||
          r.include_in_inspection === '0'
            ? 0
            : 1,
      }));
      if (!slim.inspection_scope) slim.inspection_scope = 'FULL';
    }
    mergeExtractedIntoForm(slim);
    setPendingCsExtract(slim);
    setCsDbResult(null);
    const resolvedPdf =
      pdfMeta?.path && pdfMeta?.filename
        ? { path: pdfMeta.path, filename: pdfMeta.filename }
        : slim.uploaded_pdf_path
          ? { path: slim.uploaded_pdf_path, filename: slim.uploaded_pdf_filename }
          : null;
    if (resolvedPdf) setPendingCsPdf(resolvedPdf);

    const ssCount = slim.ss_instances?.length || slim.ss_codes?.length || 0;
    const extractedFields = slim.extracted_fields || [];
    const hasDates =
      !!(slim.compliance_schedule_anniversary || slim.cs_issue_date || slim.due_date);
    const hasBuildingBits = !!(
      slim.building_name ||
      slim.address ||
      slim.compliance_schedule_number ||
      slim.owner_name ||
      slim.owner_email
    );
    const hasAnything = extractedFields.length > 0 || ssCount > 0 || hasDates || hasBuildingBits;

    if (!hasAnything) {
      toast.error(
        'PDF uploaded, but no SS/unit data could be extracted. Check it is a Compliance Schedule PDF.'
      );
      return;
    }

    if (!hasDates) {
      toast.error(
        'Could not read anniversary/issue date from this PDF. Enter CS Issue Date manually.',
        { duration: 6000 }
      );
    }

    // Existing building: write extract into DB so Detail / Units / SS hub populate immediately
    if (buildingIdForApply && buildingIdForApply !== 'new') {
      setConfirmingCs(true);
      try {
        const summary = await applyCsExtractToDb(slim, resolvedPdf, buildingIdForApply);
        setPendingCsExtract(null);
        setPendingCsPdf(null);
        const applied = Array.isArray(summary?.appliedFields) ? summary.appliedFields : [];
        const fieldHint =
          applied.length > 0
            ? ` · fields: ${applied.filter((f) => f !== 'ss_instances' && f !== 'cs_pdf' && f !== 'cs_extract_audit' && f !== 'confirmed_checklists').slice(0, 6).join(', ')}`
            : hasBuildingBits || hasDates
              ? ' · building/owner/dates saved'
              : '';
        toast.success(
          `Extracted & saved: ${summary?.ssTotal ?? ssCount} SS, ${summary?.unitsTotal ?? 0} units` +
            (summary?.checklistsSeeded ? `, ${summary.checklistsSeeded} checklists` : '') +
            fieldHint,
          { duration: 8000 }
        );
        if (summary?.skippedSs?.length) {
          toast.error(`Skipped unknown SS: ${summary.skippedSs.join(', ')}`, { duration: 6000 });
        }
      } catch (applyErr) {
        toast.error(
          applyErr.response?.data?.error ||
            'Extracted into the form, but failed to save to the database. Click Apply to retry.',
          { duration: 8000 }
        );
      } finally {
        setConfirmingCs(false);
      }
      return;
    }

    toast.success(
      `PDF analyzed: ${ssCount || 0} SS` +
        (hasBuildingBits || hasDates ? ' · building/owner/dates filled — Save to write to DB' : '') +
        '. Review preview, then Save Building.',
      { duration: 7000 }
    );
  };

  const confirmCsApply = async () => {
    if (!isEdit || !id || !pendingCsExtract) return;
    setConfirmingCs(true);
    try {
      const summary = await applyCsExtractToDb(pendingCsExtract, pendingCsPdf, id);
      setPendingCsExtract(null);
      setPendingCsPdf(null);
      toast.success(
        `Applied: ${summary?.ssTotal ?? 0} SS, ${summary?.unitsTotal ?? 0} units` +
          (summary?.checklistsSeeded ? `, ${summary.checklistsSeeded} checklists seeded` : ''),
        { duration: 7000 }
      );
      if (summary?.skippedSs?.length) {
        toast.error(`Skipped unknown SS: ${summary.skippedSs.join(', ')}`, { duration: 6000 });
      }
    } catch (applyErr) {
      toast.error(
        applyErr.response?.data?.error ||
          'Failed to apply Compliance Schedule to the database',
        { duration: 7000 }
      );
    } finally {
      setConfirmingCs(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const { _agencyScopeAutoPartly, ...payload } = data;
      const response = await api.post('/buildings', payload);
      let created = response.data;
      // Fallback: if server created building but did not apply CS extract, apply now
      if (created?.id && data.cs_extract && !created.cs_applied) {
        try {
          const applyRes = await api.post(`/buildings/${created.id}/apply-compliance-schedule`, {
            extracted: data.cs_extract,
            pdfPath: data.cs_pdf?.path || data.cs_extract.uploaded_pdf_path || null,
            pdfFilename: data.cs_pdf?.filename || data.cs_extract.uploaded_pdf_filename || null,
            extractMethod: data.cs_extract_method || data.cs_extract.extract_method || null,
            seedChecklists: data.seed_checklists !== false,
          });
          created = {
            ...created,
            cs_applied: true,
            ssCreated: applyRes.data?.ssCreated,
            unitsCreated: applyRes.data?.unitsCreated,
            unitsTotal: applyRes.data?.unitsTotal,
            ssTotal: applyRes.data?.ssTotal,
          };
        } catch (e) {
          created = {
            ...created,
            cs_apply_error: e.response?.data?.error || e.message || 'CS apply failed',
          };
        }
      }
      return { created, autoPartly: !!_agencyScopeAutoPartly || !!created?.agency_scope_auto };
    },
    onSuccess: async ({ created: data, autoPartly }) => {
      setPendingCsExtract(null);
      setPendingCsPdf(null);
      setCsDbResult(null);
      setAgencyScopeAutoPartly(false);
      if (data?.cs_apply_error) {
        toast.error(`Building created, but CS DB apply failed: ${data.cs_apply_error}`);
      } else if (data?.cs_applied) {
        toast.success(
          `Building created ??CS in DB (${data.ssTotal ?? data.ssCreated ?? 0} SS, ${data.unitsTotal ?? data.unitsCreated ?? 0} units)`
        );
      } else {
      toast.success('Building created successfully');
      }
      if (autoPartly) {
        toast(
          'Saved as Partly SS only — appears under Partly SS only (not Full agent). You can change this anytime on Edit Building.',
          { duration: 7000, icon: 'ℹ️' }
        );
      }
      queryClient.invalidateQueries(['buildings']);
      queryClient.invalidateQueries(['mandates', 'dashboard']);
      queryClient.invalidateQueries(['building', data.id, 'levels-setup']);
      queryClient.invalidateQueries({ queryKey: ['partly-agents'] });
      queryClient.invalidateQueries(['building', data.id, 'inspection-menu']);
      navigate(`/buildings/${data.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create building');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const { cs_extract, cs_pdf, cs_extract_method, seed_checklists, _agencyScopeAutoPartly, ...buildingFields } = data;
      const response = await api.put(`/buildings/${id}`, buildingFields);
      let applySummary = null;
      // Always re-apply pending CS on Save so SS/units land in DB even if upload-time apply failed
      if (cs_extract) {
        const applyRes = await api.post(`/buildings/${id}/apply-compliance-schedule`, {
          extracted: cs_extract,
          pdfPath: cs_pdf?.path || cs_extract.uploaded_pdf_path || null,
          pdfFilename: cs_pdf?.filename || cs_extract.uploaded_pdf_filename || null,
          extractMethod: cs_extract_method || cs_extract.extract_method || null,
          seedChecklists: seed_checklists !== false,
        });
        applySummary = applyRes.data;
      }
      return {
        building: response.data,
        applySummary,
        autoPartly: !!_agencyScopeAutoPartly || !!response.data?.agency_scope_auto,
      };
    },
    onSuccess: (result, variables) => {
      setPendingCsExtract(null);
      setPendingCsPdf(null);
      setCsDbResult(null);
      setAgencyScopeAutoPartly(false);
      const apply = result?.applySummary;
      toast.success(
        variables?.cs_extract
          ? `Building updated ??CS saved (${apply?.ssTotal ?? 0} SS, ${apply?.unitsTotal ?? 0} units in DB)`
          : 'Building updated successfully'
      );
      if (result?.autoPartly) {
        toast(
          'Saved as Partly SS only — appears under Partly SS only (not Full agent). You can change this anytime here.',
          { duration: 7000, icon: 'ℹ️' }
        );
      }
      queryClient.invalidateQueries(['buildings']);
      queryClient.invalidateQueries(['building', id]);
      queryClient.invalidateQueries(['building', id, 'ss-units']);
      queryClient.invalidateQueries(['building', id, 'ss-instances']);
      queryClient.invalidateQueries(['building', id, 'compliance-schedules']);
      queryClient.invalidateQueries(['building', id, 'levels-setup']);
      queryClient.invalidateQueries(['mandates', 'dashboard']);
      queryClient.invalidateQueries({ queryKey: ['partly-agents'] });
      queryClient.invalidateQueries(['building', id, 'inspection-menu']);
      navigate(variables?.cs_extract ? `/buildings/${id}/units` : `/buildings/${id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update building');
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const csIssue = String(formData.compliance_schedule_anniversary || '').trim().slice(0, 10);
    const dates = csIssue
      ? {
          compliance_schedule_anniversary: csIssue,
          due_date: computeNextBwofDueFromAnniversary(csIssue) || csIssue,
        }
      : { compliance_schedule_anniversary: null };
    if (formData.agency_scope !== 'FULL_AGENT' && formData.agency_scope !== 'SS_ONLY') {
      toast.error('Choose Full or Partial under Owner / Customer.');
      return;
    }
    const agencyScope = formData.agency_scope;
    const inspectionScope = agencyScope === 'SS_ONLY' ? 'PARTIAL' : 'FULL';

    const submitData = {
      ...formData,
      ...dates,
      agency_scope: agencyScope,
      is_bwof_agent: agencyScope === 'FULL_AGENT',
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      _agencyScopeAutoPartly: agencyScopeAutoPartly && agencyScope === 'SS_ONLY',
      partly_agent_id: agencyScope === 'SS_ONLY' ? formData.partly_agent_id || null : null,
      partly_agent_name:
        agencyScope === 'SS_ONLY' && !formData.partly_agent_id
          ? String(formData.partly_agent_name || '').trim() || undefined
          : undefined,
      partly_agent_phone:
        agencyScope === 'SS_ONLY' && !formData.partly_agent_id
          ? String(formData.partly_agent_phone || '').trim() || undefined
          : undefined,
      partly_agent_email:
        agencyScope === 'SS_ONLY' && !formData.partly_agent_id
          ? String(formData.partly_agent_email || '').trim() || undefined
          : undefined,
      partly_agent_address:
        agencyScope === 'SS_ONLY' && !formData.partly_agent_id
          ? String(formData.partly_agent_address || '').trim() || undefined
          : undefined,
      clear_partly_agent: agencyScope !== 'SS_ONLY',
    };
    submitData.inspection_scope = inspectionScope;
    submitData.operation_start_date = String(formData.operation_start_date || '').trim().slice(0, 10) || null;
    delete submitData.contract_end_date;
    if (!csIssue) delete submitData.due_date;

    if (deviceChecksReady) {
      submitData.inspection_modules = deviceChecks;
      submitData.has_chiller = !!deviceChecks.CHILLER;
    }

    // Attach CS extract on submit so create/update always persist SS/units to DB
    if (pendingCsExtract) {
      submitData.cs_extract = pendingCsExtract;
      submitData.cs_pdf = pendingCsPdf?.path
        ? pendingCsPdf
        : pendingCsExtract.uploaded_pdf_path
          ? {
              path: pendingCsExtract.uploaded_pdf_path,
              filename: pendingCsExtract.uploaded_pdf_filename,
            }
          : undefined;
      submitData.cs_extract_method = pendingCsExtract.extract_method || null;
      submitData.seed_checklists = seedCsChecklists;
    }
    if (isEdit) {
      try {
        await ssSectionRef.current?.persistScope();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Could not save the SS selection');
        return;
      }
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingPdf(true);
    try {
      // Upload PDF ??put type BEFORE file so multer destination sees the folder
      const uploadForm = new FormData();
      uploadForm.append('type', 'compliance-schedule');
      uploadForm.append('file', file);

      const uploadResponse = await api.post('/files/upload', uploadForm, {
        headers: {
          'x-upload-type': 'compliance-schedule',
        },
        params: { type: 'compliance-schedule' },
        timeout: 0,
      });

      setUploadedPdfUrl(uploadResponse.data.url);
      toast.success('PDF uploaded successfully');

      // Analyze PDF
      setAnalyzingPdf(true);
      try {
        const analyzeResponse = await api.post('/buildings/analyze-compliance-schedule', {
          filePath: uploadResponse.data.path || null,
          filename: uploadResponse.data.filename,
          buildingId: id && id !== 'new' ? id : null,
        });

        const extractedData = analyzeResponse.data.data || {};
        if (analyzeResponse.data.warning) {
          toast.error(analyzeResponse.data.warning, { duration: 6000 });
        }
        const pdfMeta = {
          path: uploadResponse.data.path || extractedData.uploaded_pdf_path || null,
          filename: uploadResponse.data.filename,
        };
        await processCsExtract(extractedData, id && id !== 'new' ? id : null, pdfMeta);

        if (id && id !== 'new') {
          queryClient.invalidateQueries(['building', id, 'compliance-schedules']);
        }
      } catch (analyzeError) {
        console.error('PDF analysis error:', analyzeError);
        const msg =
          analyzeError.response?.data?.error ||
          analyzeError.response?.data?.message ||
          (analyzeError.response
            ? `Analyze failed (${analyzeError.response.status})`
            : 'Failed to analyze PDF (network). File was uploaded ??try Analyze again.');
        toast.error(msg, { duration: 8000 });
      } finally {
        setAnalyzingPdf(false);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (!error.response) {
        toast.error('Failed to upload PDF');
      }
    } finally {
      setUploadingPdf(false);
    }
  };

  const analyzeSavedPdf = async (pdf) => {
    try {
      setAnalyzingPdf(true);
      const analyzeResponse = await api.post('/buildings/analyze-compliance-schedule', {
        filePath: pdf.path,
        filename: pdf.filename,
        buildingId: id,
      });
      const extractedData = analyzeResponse.data.data || {};
      await processCsExtract(extractedData, id, {
        path: pdf.path,
        filename: pdf.filename,
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to analyze PDF');
    } finally {
      setAnalyzingPdf(false);
    }
  };

  return (
    <div>
      <BuildingPageHeading
        buildingId={isEdit ? id : undefined}
        title={isEdit ? 'Edit Building' : 'New building'}
      />
      {isEdit && isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (

      <form
        onSubmit={handleSubmit}
        className="bg-slate-100/80 rounded-lg p-4 sm:p-6 [&_input:not([type=checkbox]):not([type=radio]):not([type=file]):not(.hidden)]:bg-green-100 [&_select]:bg-green-100 [&_textarea]:bg-green-100"
      >
        <div className="space-y-8">
          {/* Building Information — name, address, dates (Overview-aligned) */}
          <div className="p-5 rounded-xl border-2 border-green-300 bg-green-100 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
              Building Information
              <InfoButton title="Building Information">
                <p>
                  Building name, address, CS Issue Date, contract start date, Compliance Schedule number, and
                  initial inspection status. The BWOF due date is taken from the CS Issue Date. The contract
                  start date is used only for paperwork years on SS forms. Inspection due dates stay on the
                  CS / SS anniversary.
                </p>
                <p>
                  Choose Full or Partial under Owner / Customer. The Specified Systems list follows that
                  choice. Structural parts and drawings are in <strong>Building structure &amp; drawings</strong>.
                </p>
              </InfoButton>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="flex items-start md:col-span-2">
                <Building2 className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Building name *</p>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                    placeholder="e.g. Mrs. Sushi, Harbour View Apartments"
            />
          </div>
              </div>
              <div className="flex items-start md:col-span-2">
                <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Address *</p>
            <input
              type="text"
              id="address"
              name="address"
              required
              value={formData.address}
              onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                    placeholder="Street address"
            />
                </div>
              </div>
          </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">CS Issue Date</p>
            <input
              type="date"
                    id="compliance_schedule_anniversary"
                    name="compliance_schedule_anniversary"
                    value={formData.compliance_schedule_anniversary}
              onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
            />
                </div>
          </div>

              <div className="flex items-start">
                <Building2 className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1 inline-flex flex-wrap items-center gap-x-2">
                    Compliance Schedule
                    {isEdit && savedPdfs && savedPdfs.length > 0 ? (
                      <Link
                        to={`/buildings/${id}/compliance-schedule`}
                        className="text-primary-700 hover:underline font-medium"
                      >
                        View copy
                      </Link>
                    ) : null}
                  </p>
                  <input
                    type="text"
                    id="compliance_schedule_number"
                    name="compliance_schedule_number"
                    value={formData.compliance_schedule_number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                    placeholder="CS number"
                  />
                </div>
              </div>

              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Contract / start date</p>
                  <input
                    type="date"
                    id="operation_start_date"
                    name="operation_start_date"
                    value={formData.operation_start_date}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                  />
                </div>
              </div>

              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Initial Inspection Status</p>
            <select
              id="initial_inspection_status"
              name="initial_inspection_status"
              value={formData.initial_inspection_status}
              onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
            >
              <option value="">Select status</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
            </select>
                </div>
              </div>
          </div>

          </div>

          {/* Owner / Customer — separate card under Building Information */}
          <div className="p-5 rounded-xl border-2 border-green-300 bg-green-100 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
              Owner / Customer (from CS)
              <InfoButton title="Owner / Customer (from CS)">
                <p>
                  Choose whether this building’s inspection covers the whole Compliance Schedule or only some
                  Specified Systems. Owner contact details sit under that choice. They are usually filled from
                  the Compliance Schedule PDF and can be edited here.
                </p>
                <p>
                  <strong>Full</strong> — we cover the entire building.                   <strong>Partial</strong> — we cover
                  selected systems only (for example SS 9). The External Agent below is optional.
                </p>
              </InfoButton>
            </h2>
            <fieldset className="mb-6 rounded-lg border border-green-300 bg-green-100 px-3 py-3">
              <legend className="px-1 text-sm font-semibold text-gray-800">Inspection coverage</legend>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="inspection_coverage"
                    checked={formData.agency_scope === 'FULL_AGENT'}
                    onChange={() => applyCoverage('FULL', { driveSs: true })}
                    className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">Full</span>
                    <span className="block text-xs text-gray-500">The entire building</span>
                  </span>
            </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="inspection_coverage"
                    checked={formData.agency_scope === 'SS_ONLY'}
                    onChange={() => applyCoverage('PARTIAL', { driveSs: true })}
                    className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">Partial</span>
                    <span className="block text-xs text-gray-500">Selected SS only, for example SS 9</span>
                  </span>
                </label>
              </div>
            </fieldset>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <User className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Owner name</p>
            <input
              type="text"
                    id="owner_name"
                    name="owner_name"
                    value={formData.owner_name}
              onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                    placeholder="Building owner / customer"
            />
          </div>
              </div>
              <div className="flex items-start">
                <Phone className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Phone</p>
                  <input
                    type="text"
                    id="owner_telephone_work"
                    name="owner_telephone_work"
                    value={formData.owner_telephone_work}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                  />
                </div>
              </div>
              <div className="flex items-start">
                <Mail className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Email</p>
                  <input
                    type="email"
                    id="owner_email"
                    name="owner_email"
                    value={formData.owner_email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                  />
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">Mailing address</p>
                  <input
                    type="text"
                    id="owner_mailing_address"
                    name="owner_mailing_address"
                    value={formData.owner_mailing_address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                  />
                </div>
              </div>
            </div>
            {formData.agency_scope === 'SS_ONLY' ? (
              <div className="mt-6 border-t border-slate-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex flex-wrap items-center gap-2">
                  <Link2 className="w-4 h-4 text-sky-700" />
                  External Agent
                  <InfoButton title="External Agent">
                    <p>
                      Optional when coverage is Partial. This is the company that holds the rest of the BWOF.
                      Pick a company from the list, or enter any of the company name, phone, email and address.
                      A new company name is saved to Registered Company → Partly agent.
                    </p>
                  </InfoButton>
                  <Link
                    to="/network?tab=partly-agent"
                    className="ml-auto text-xs font-semibold text-primary-700 hover:underline"
                  >
                    Open External Agent list
                  </Link>
                </h3>
                    <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Registered company</p>
                      <select
                    value={formData.partly_agent_id || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      const hit = partlyAgents.find((a) => String(a.id) === String(v));
                      setFormData((prev) => ({
                        ...prev,
                        partly_agent_id: v,
                        partly_agent_name: hit?.name || '',
                        partly_agent_phone: hit?.phone || '',
                        partly_agent_email: hit?.email || '',
                        partly_agent_address: hit?.address || '',
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 bg-green-100"
                  >
                    <option value="">— Enter a new company below —</option>
                    {partlyAgents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                {formData.partly_agent_id ? (
                  <p className="mt-2 text-sm text-gray-700">
                    {[
                      formData.partly_agent_phone,
                      formData.partly_agent_email,
                      formData.partly_agent_address,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'No phone, email or address stored for this company.'}
                  </p>
                ) : (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Company name</p>
                      <input
                        type="text"
                        value={formData.partly_agent_name || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            partly_agent_id: '',
                            partly_agent_name: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                        placeholder="e.g. ABC Compliance Ltd"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Phone</p>
                      <input
                        type="text"
                        value={formData.partly_agent_phone || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, partly_agent_phone: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Email</p>
                      <input
                        type="email"
                        value={formData.partly_agent_email || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, partly_agent_email: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                      />
                  </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Address</p>
                      <input
                        type="text"
                        value={formData.partly_agent_address || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, partly_agent_address: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900"
                      />
                  </div>
                </div>
                            )}
                          </div>
            ) : null}
                        </div>

          {isEdit ? (
            <div className="space-y-8">
              <FloorStructureSetup
                buildingId={id}
                buildingName={formData.name || building?.name}
                buildingFloorPlanUrl={building?.floor_plan_url || formData.floor_plan_url || null}
                embed
                workerCanMoveUnitMarkers={!!formData.worker_can_move_unit_markers}
                onWorkerCanMoveUnitMarkersChange={(checked) =>
                  setFormData((prev) => ({ ...prev, worker_can_move_unit_markers: checked }))
                }
                isDefaultForWorkers={!!formData.is_default_for_workers}
                onIsDefaultForWorkersChange={(checked) =>
                  setFormData((prev) => ({ ...prev, is_default_for_workers: checked }))
                }
                onComplete={() => navigate(`/buildings/${id}/floor-plan`)}
              />
                </div>
          ) : null}

          {isEdit ? (
            <BuildingSsSection
              ref={ssSectionRef}
              buildingId={id}
              building={building}
              ssCodes={ssCodes}
              drivenScope={
                formData.agency_scope === 'SS_ONLY'
                  ? 'PARTIAL'
                  : formData.agency_scope === 'FULL_AGENT'
                    ? 'FULL'
                    : ''
              }
              coverageRev={coverageRev}
              onScopeChange={(mode) => applyCoverage(mode, { driveSs: false })}
            />
          ) : null}

          {/* Compliance Schedule PDF — single upload zone (writes SS + units to DB) */}
          <div className="p-5 rounded-xl border-2 border-green-300 bg-green-100 shadow-sm">
            <div className="flex items-center justify-between mb-2 border-b border-emerald-200 pb-3">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <h3 className="text-base font-semibold text-emerald-950 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                Compliance Schedule PDF
                </h3>
                <InfoButton title="Compliance Schedule PDF">
                  <p>
                    Upload the building Compliance Schedule. The system extracts owner, dates, SS codes and creates
                    stub SS units in the database (for jobs, app sync, and floor plan). This is the correct upload —
                    CCC only fills name/address and does not create SS units.
                  </p>
                  <p>PDF only, maximum 10MB. After upload, review extracted SS then confirm to save.</p>
                </InfoButton>
              </div>
              {isEdit && savedPdfs && savedPdfs.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowSavedPdfs(!showSavedPdfs)}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  {showSavedPdfs ? 'Hide' : 'Show'} Saved ({savedPdfs.length})
                </button>
              )}
            </div>

            {isEdit && showSavedPdfs && savedPdfs && savedPdfs.length > 0 && (
              <div className="mb-3 p-3 bg-card rounded-lg border border-emerald-100">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Saved Compliance Schedule PDFs</h4>
                <div className="space-y-2">
                  {savedPdfs.map((pdf, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-green-100 rounded-lg border border-green-300"
                    >
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <FileText className="w-4 h-4 text-red-600 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{pdf.originalName}</p>
                          <p className="text-xs text-gray-500">
                            {(pdf.size / 1024).toFixed(1)} KB · {new Date(pdf.uploadedAt).toLocaleDateString()}
                          </p>
                          </div>
                        </div>
                        <button
                          type="button"
                        onClick={() => analyzeSavedPdf(pdf)}
                          disabled={analyzingPdf}
                          className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                        >
                          Use This PDF
                        </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-green-300 border-dashed rounded-lg cursor-pointer bg-green-100 hover:bg-green-200">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {uploadingPdf || analyzingPdf ? (
                    <>
                      <Loader className="w-8 h-8 mb-2 text-primary-600 animate-spin" />
                      <p className="text-sm text-gray-500">
                      {uploadingPdf ? 'Uploading...' : 'Analyzing PDF & saving SS/units...'}
                      </p>
                    </>
                  ) : (
                    <>
                    <Upload className="w-8 h-8 mb-2 text-emerald-600" />
                    <p className="mb-1 text-sm text-gray-600">
                      <span className="font-semibold">Click to upload</span> Compliance Schedule PDF
                    </p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handlePdfUpload}
                  disabled={uploadingPdf || analyzingPdf}
                  className="hidden"
                />
              </label>

              {uploadedPdfUrl && (
                <div className="mt-2 flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-green-600" />
                  <a
                    href={uploadedPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-800"
                  >
                    View uploaded PDF
                  </a>
                </div>
              )}

            {csDbResult && (
              <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded-lg">
                <p className="text-sm font-medium text-emerald-900">
                  In database: {csDbResult.ssTotal} SS · {csDbResult.unitsTotal} units
                  {csDbResult.checklistsSeeded != null
                    ? ` · ${csDbResult.checklistsSeeded} checklists seeded`
                    : ''}
                </p>
                <p className="text-xs text-emerald-700 mt-1">
                  This apply: +{csDbResult.ssCreated} SS, +{csDbResult.unitsCreated} units.
                  {isEdit && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="underline font-medium"
                        onClick={() => navigate(`/buildings/${id}`)}
                      >
                        Open Building Detail
                      </button>
                      {' · '}
                      <button
                        type="button"
                        className="underline font-medium"
                        onClick={() => navigate(`/buildings/${id}/units`)}
                      >
                        Open SS Units
                      </button>
                    </>
                  )}
                </p>
              </div>
            )}

            {pendingCsExtract &&
              !csDbResult &&
              ((pendingCsExtract.ss_instances?.length || 0) > 0 ||
                (pendingCsExtract.extracted_fields?.length || 0) > 0 ||
                pendingCsExtract.building_name ||
                pendingCsExtract.compliance_schedule_number) && (
              <CsApplyPreview
                extract={pendingCsExtract}
                csDbResult={null}
                isEdit={isEdit}
                seedChecklists={seedCsChecklists}
                onSeedChecklistsChange={setSeedCsChecklists}
                confirming={confirmingCs}
                onConfirm={confirmCsApply}
                onDiscard={() => {
                  setPendingCsExtract(null);
                  setPendingCsPdf(null);
                  setCsDbResult(null);
                }}
                onChangeRows={(nextRows) =>
                  setPendingCsExtract((prev) =>
                    prev ? { ...prev, ss_instances: nextRows } : prev
                  )
                }
                onChangeScope={(nextScope) => {
                  const s = String(nextScope || 'FULL').toUpperCase() === 'PARTIAL' ? 'PARTIAL' : 'FULL';
                  applyCoverage(s, { driveSs: false });
                }}
              />
            )}

          </div>

          {/* Field options */}
          <div className="p-5 rounded-xl border-2 border-green-300 bg-green-100 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2 border-b border-slate-200 pb-3">
              <Wrench className="w-5 h-5 text-gray-500" />
              Field options
            </h2>
            <div className="space-y-3">
              {inspectionDevices.map((device) => {
                const code = String(device.module_code || '').toUpperCase();
                const label = String(device.label || code).split('/')[0].trim();
                return (
                  <label key={code} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!deviceChecks[code]}
                      onChange={(e) => {
                        deviceChecksTouched.current = true;
                        const checked = e.target.checked;
                        setDeviceChecks((prev) => ({ ...prev, [code]: checked }));
                        if (code === 'CHILLER') {
                          setFormData((prev) => ({ ...prev, has_chiller: checked }));
                        }
                      }}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-x-2">
                        <span className="text-sm font-medium text-gray-900">{label}</span>
                        <InfoButton title={label}>
                          <p>
                            When checked, this building’s menu shows <strong>{label}</strong> after SS Units.
                            The page matches the Chiller screen: assets, checklist, schedule, and floor plan.
                          </p>
                        </InfoButton>
                      </span>
                    </span>
                </label>
                );
              })}
              {!inspectionDevices.length && (
                <p className="text-sm text-gray-500">No inspection devices yet. Add them in Settings → Inspection module.</p>
              )}
            </div>
          </div>

          {/* Building Documents — company defaults + mapped storage */}
          {isEdit && id !== 'new' && (
            <div className="md:col-span-2 p-5 rounded-xl border-2 border-green-300 bg-green-100 shadow-sm">
              <div className="flex items-center justify-between mb-2 border-b border-slate-200 pb-3">
                <h3 className="text-base font-semibold text-gray-900 flex flex-wrap items-center gap-2">
                  <Folder className="w-5 h-5" />
                  Building Documents
                  <InfoButton title="Building Documents">
                    <p>
                      Uses company document folders as defaults. Map a real storage location before enabling.
                    </p>
                    <p>
                      Certificate copies are named{' '}
                      <code className="bg-gray-100 px-1 rounded">date_SS_buildingInitials</code>.
                    </p>
                    <p>
                      Default storage root comes from company settings; override per building if needed.
                    </p>
                  </InfoButton>
                </h3>
                <button
                  type="button"
                  onClick={() => setShowFolders(!showFolders)}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  {showFolders ? 'Hide' : 'Show'} settings
                </button>
              </div>
              {showFolders && (
                <div className="mt-3 space-y-4">
                  {folderFetching && !folderData ? (
                    <p className="text-sm text-gray-500">Loading document settings…</p>
                  ) : folderError ? (
                    <p className="text-sm text-red-600">
                      {folderErrObj?.response?.data?.error ||
                        folderErrObj?.message ||
                        'Failed to load document settings'}
                    </p>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Storage location (map first)
                        </label>
                        <input
                          type="text"
                          value={docStorageRoot}
                          onChange={(e) => setDocStorageRoot(e.target.value)}
                          placeholder={folderData?.default_storage_root || 'Absolute path on server'}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                        <input
                          type="checkbox"
                          checked={docEnabled}
                          onChange={(e) => setDocEnabled(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600"
                        />
                        Enable documents for this building (requires mapped path)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={saveDocSettingsMutation.isLoading}
                          onClick={() =>
                            saveDocSettingsMutation.mutate({
                              storage_root: docStorageRoot,
                              enabled: docEnabled,
                              use_company_defaults: true,
                              sync_defaults: true,
                            })
                          }
                          className="px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white font-semibold disabled:opacity-50"
                        >
                          Save &amp; sync company defaults
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`/buildings/${id}/documents`)}
                          className="px-3 py-1.5 text-sm rounded-lg border border-green-300 bg-green-100 hover:bg-green-200"
                        >
                          Open Documents
                        </button>
                    </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-2">Folders</p>
                        <ul className="bg-green-100 border border-green-300 rounded-lg divide-y max-h-56 overflow-y-auto">
                          {(folderData?.folders || []).map((f) => (
                            <li
                              key={f.path}
                              className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                            >
                              <span className="truncate">
                                {f.label || f.name}
                                {f.isSystem ? (
                                  <span className="ml-2 text-[10px] text-slate-400">company</span>
                                ) : null}
                              </span>
                              {!f.isSystem ? (
                                <button
                                  type="button"
                                  className="text-xs text-red-600 font-semibold shrink-0"
                                  onClick={() => {
                                    if (window.confirm(`Delete folder “${f.label || f.name}”?`)) {
                                      deleteFolderMutation.mutate(f.path);
                                    }
                                  }}
                                >
                                  Delete
                                </button>
                              ) : null}
                            </li>
                          ))}
                          {!(folderData?.folders || []).length ? (
                            <li className="px-3 py-4 text-xs text-gray-500 text-center">
                              No folders yet — save to sync company defaults.
                            </li>
                          ) : null}
                        </ul>
                        <div className="mt-2 flex gap-2">
                          <input
                            type="text"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            placeholder="New folder name"
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                          />
                          <button
                            type="button"
                            disabled={!newFolderName.trim() || addFolderMutation.isLoading}
                            onClick={() => addFolderMutation.mutate(newFolderName.trim())}
                            className="px-3 py-1.5 text-sm rounded-lg bg-slate-800 text-white font-semibold disabled:opacity-50"
                          >
                            Add folder
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          {isEdit && building?.status !== 'INACTIVE' ? (
            <InfoButton title="Shelve building" className="text-xs">
              <p>
                To shelve this building from work queues, use <strong>Starts later</strong> or{' '}
                <strong>Ended</strong> on the Buildings list. Data is kept; restore from those tabs anytime.
              </p>
            </InfoButton>
          ) : (
            <span />
          )}
          <div className="flex items-center space-x-4 ml-auto">
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/buildings/${id}` : '/buildings')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading || updateMutation.isLoading}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {createMutation.isLoading || updateMutation.isLoading
              ? 'Saving...'
              : isEdit
              ? 'Update Building'
              : 'Create Building'}
          </button>
          </div>
        </div>
      </form>
      )}
    </div>
  );
}

export default BuildingForm;
